import { z } from 'zod';

/**
 * Outbound mail caps — the numbers, shared by the worker that enforces them,
 * the API that validates them and the screen that sets them.
 *
 * The decision itself is `decideOutbound` in @esa/core. This is only what a
 * tenant may configure and what it gets if it configures nothing.
 */

export interface OutboundCapNumbers {
  perHour: number;
  perDay: number;
  perRecipientPerDay: number;
}

/** What a tenant gets when it has configured nothing. The n8n desk's values. */
export const OUTBOUND_CAP_DEFAULTS: Readonly<OutboundCapNumbers> = Object.freeze({
  perHour: 20,
  perDay: 100,
  perRecipientPerDay: 5,
});

/**
 * The most any tenant may configure. Platform-owned, changed by deploy.
 *
 * Constants rather than environment variables on purpose: a ceiling that can
 * be mistyped in a variable group is one more way to remove a cap.
 */
export const OUTBOUND_CAP_CEILINGS: Readonly<OutboundCapNumbers> = Object.freeze({
  perHour: 200,
  perDay: 1000,
  perRecipientPerDay: 20,
});

/**
 * What a tenant admin may save. Each value optional — absent means "use the
 * default", which is different from any number.
 *
 * The worker does not trust this: `resolveCaps` re-checks every value at send
 * time, so a row written some other way still cannot remove a cap.
 */
export const outboundSettingsSchema = z
  .object({
    maxPerHour: z.number().int().min(1).max(OUTBOUND_CAP_CEILINGS.perHour),
    maxPerDay: z.number().int().min(1).max(OUTBOUND_CAP_CEILINGS.perDay),
    maxPerRecipientPerDay: z.number().int().min(1).max(OUTBOUND_CAP_CEILINGS.perRecipientPerDay),
  })
  .partial()
  .strict();
export type OutboundSettings = z.infer<typeof outboundSettingsSchema>;

/** A breaker reset says why. "Reset" with no reason is not an audit trail. */
export const breakerResetSchema = z.object({
  note: z.string().trim().min(3).max(500),
});
